import { useMemo } from 'react'
import { Autocomplete, Box, Chip, TextField } from '@mui/material'
import _ from 'lodash'
import { useGetAllActiveMerchantsIdAndNameCachedQuery } from '../../redux/commonApiCalls'

/**
 * Searchable merchant dropdown for the AllocationConfig tabs. Same cached list as
 * Forward Shipments' MerchantFilterCached, but controlled (these tabs don't use
 * react-hook-form). onChange receives the full merchant object — callers pick the
 * identifier they need: `.id` (Mongo OID, e.g. merchant-config API) or
 * `.merchantId` (numeric id used by knobs scope / shadow endpoints).
 *
 * `excludeIds` (optional, default none) drops specific merchants from the
 * pickable list by `.id` — e.g. the leadership dashboard's mapping picker uses
 * this to hide merchants that already have an owner, so a fresh "map merchant"
 * flow can't silently overwrite an existing mapping. Doesn't affect `value`'s
 * own display — an already-selected merchant still renders even if excluded.
 *
 * `tagFor` (optional) returns `{ label, color, rank }` or null per merchant: the tag shows
 * as a chip on the option, and tagged merchants sort first by `rank` (e.g. PACE
 * Analytics tags each merchant Live / Shadow).
 */
export default function MerchantSelect({
  value, onChange, label = 'Select Merchant', size = 'small', fullWidth = true, disabled = false,
  excludeIds, tagFor,
}) {
  const { data: merchants = [], isFetching } = useGetAllActiveMerchantsIdAndNameCachedQuery()
  const options = useMemo(() => {
    const active = merchants.filter((m) => m.is_deleted === '0')
    const filtered = excludeIds?.length ? active.filter((m) => !excludeIds.includes(m.id)) : active
    if (!tagFor) return _.orderBy(filtered, 'name')
    return _.orderBy(filtered, [(m) => tagFor(m)?.rank ?? 99, 'name'])
  }, [merchants, excludeIds, tagFor])
  const label_ = (m) => (m.companyName ? `${m.name} (${m.companyName})` : m.name) || ''
  return (
    <Autocomplete
      options={options}
      value={value || null}
      loading={isFetching}
      disabled={disabled}
      fullWidth={fullWidth}
      size={size}
      getOptionLabel={label_}
      renderOption={tagFor ? (props, m) => {
        const tag = tagFor(m)
        return (
          <Box component="li" {...props} key={m.id} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
            <span>{label_(m)}</span>
            {tag && <Chip size="small" label={tag.label} color={tag.color || 'default'} variant="outlined" />}
          </Box>
        )
      } : undefined}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      onChange={(_e, m) => onChange(m)}
      renderInput={(params) => <TextField {...params} label={label} placeholder="Search merchant…" />}
    />
  )
}
